<template>
    <RedesignBaseModal
        :visible="visible"
        :title="`${$t('dashboard.groupTemplatesModal')}`"
        width="80%"
        @update:visible="onClose"
        @close="onClose"
    >
        <div class="w-full">
            <div class="w-full">
                <RedesignBaseForm
                    ref="baseFormRef"
                    class="flex flex-col md:flex-row gap-4 items-center"
                >
                    <RedesignFormItem
                        prop="AccountID"
                        :label="`${$t('dashboard.widgetGroupTitle')}`"
                        class="flex-grow max-w-80"
                    >
                        <RedesignBaseInput
                            v-model="widgetGroupTitle"
                        />
                    </RedesignFormItem>
                    <RedesignFormItem
                        prop="Order"
                        :label="`${$t('dashboard.widgetGroupOrder')}`"
                    >
                        <RedesignInputNumber
                            v-model="widgetGroupOrder"
                            :min-value="1"
                            :max-value="maxOrder"
                        />
                    </RedesignFormItem>
                </RedesignBaseForm>
            </div>
            <div class="w-full font-semibold text-main-text-color mb-3">
                {{ $t('general.templates') }}
            </div>
            <div class="w-full mb-5">
                <TemplatesLanguageSelector
                    v-model="templatesLanguageFilterValue"
                    bordered
                />
            </div>
            <div class="w-full flex gap-4">
                <div class="flex-grow min-w-0">
                    <DashboardTemplatesTabs
                        ref="dashboardTemplatesTabsRef"
                        v-model="selectedTemplate"
                        :loading="dashboardTemplatesLoading"
                        :template-categories="dashboardTemplates"
                        :language-filter-value="templatesLanguageFilterValue"
                    />
                </div>
                <!-- Template Description - right side panel -->
                <div
                    v-if="selectedTemplate"
                    class="flex-shrink-0 w-64"
                >
                    <DashboardTemplateDescription
                        :template="selectedTemplate"
                    />
                </div>
            </div>
        </div>
        <template #footer>
            <div class="w-full flex md:items-center justify-between flex-col md:flex-row gap-3">
                <div
                    v-if="selectedTemplate"
                    class="flex-grow text-left flex items-center gap-2"
                >
                    {{ $t('dashboard.selectedTemplate', {templateName: selectedTemplate?.DashboardTemplateName}) }}
                    <RedesignBaseIconButton
                        icon="X"
                        :size="20"
                        @click="selectedTemplate = undefined"
                    />
                </div>
                <div class="ml-auto flex items-center gap-3">
                    <RedesignBaseButton
                        type="outline"
                        color="secondary"
                        @click="onClose"
                    >
                        {{ $t('common.cancel') }}
                    </RedesignBaseButton>

                    <RedesignBaseButton
                        :loading="isLoading"
                        :disabled="!selectedTemplate"
                        @click="onSave"
                    >
                        {{ $t('common.save') }}
                    </RedesignBaseButton>
                </div>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import TemplatesLanguageSelector from '@/views/DashboardCreation/components/TemplatesLanguageSelector.vue'
import { LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE } from '@/enum/default-layout-config'
import DashboardTemplatesTabs from '@/views/DashboardCreation/DashboardTemplatesTabs.vue'
import { DashboardTemplateList } from '@/types/dashboard-template'
import useDashboardTemplates from '@/composables/useDashboardTemplates'
import { activeLanguage } from '@/composables/globalData/useLanguages'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import DashboardTemplateDescription from '@/views/DashboardCreation/components/DashboardTemplateDescription.vue'
import i18n from '@/i18n'
import { WidgetGroupListCreate } from '@/types/dashboard'
import { isGlobalLoading } from '@/composables/useGlobalLoader'

const props = withDefaults(
    defineProps<{
        loading?: boolean
        groupsCount?: number
    }>(),
    {
        groupsCount: 0
    }
)

const emit = defineEmits<{
    (e: 'save', pl: WidgetGroupListCreate): void
}>()

/* Data */
const { closeWithConfirm } = useConfirmBeforeClose()
const visible = ref(false)
const widgetGroupTitle = ref(i18n.t('common.newGroup')?.toString())
const widgetGroupOrder = ref(1)

const templatesLanguageFilterValue = ref(LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE)
const selectedTemplate = ref<DashboardTemplateList | undefined>(undefined)

/* Computed */
const maxOrder = computed(() => {
    return props.groupsCount + 1
})

/* Composable */
const {
    dashboardTemplatesLoading,
    dashboardTemplates,
    fetchDashboardTemplates,
    getWidgetListFromTemplate
} = useDashboardTemplates()

const defaultGroupTitle = computed(() => i18n.t('common.newGroup')?.toString() ?? '')

const allTemplateNames = computed(() =>
    (dashboardTemplates.value ?? []).flatMap((cat) =>
        (cat.DashboardTemplateList ?? []).map((t) => t.DashboardTemplateName)
    )
)

const isLoading = computed(() => {
    return props.loading || isGlobalLoading.value || dashboardTemplatesLoading.value
})

function onOpen () {
    visible.value = true
}

function forceCloseModal () {
    visible.value = false
    templatesLanguageFilterValue.value = LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE
    selectedTemplate.value = undefined
    widgetGroupTitle.value = defaultGroupTitle.value
    widgetGroupOrder.value = 1
}

function onClose () {
    closeWithConfirm(forceCloseModal)
}

function onSave () {
    emit('save', {
        WidgetGroupTitle: widgetGroupTitle.value,
        Order: widgetGroupOrder.value - 1,
        WidgetList: getWidgetListFromTemplate(selectedTemplate.value)
    })
}

watch(
    activeLanguage,
    async (value) => {
        await fetchDashboardTemplates()

        if (!value || !value.LanguageID) {
            templatesLanguageFilterValue.value = LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE
        } else {
            templatesLanguageFilterValue.value = value.LanguageID
        }
    },
    {
        deep: true,
        immediate: true
    }
)

watch(
    selectedTemplate, 
    (value) => {
        if (
            !widgetGroupTitle.value ||
            widgetGroupTitle.value === defaultGroupTitle.value ||
            allTemplateNames.value.includes(widgetGroupTitle.value)
        ) {
            widgetGroupTitle.value = value?.DashboardTemplateName ?? defaultGroupTitle.value
        }
    }
)

defineExpose({
    open: onOpen,
    close: forceCloseModal
})
</script>

